import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, FolderOpen, Calendar, Image as ImageIcon, Trash2, Share2, Copy, Search, Edit, Mail, X, Star, ExternalLink, QrCode, MoreHorizontal, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { StatTile, StatusPill, Btn } from './admin/cockpit/Cockpit';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const GalleriesView = ({ galleries, onRefresh, axiosConfig, backendUrl, userRole = 'admin' }) => {
  const isAdmin = userRole === 'admin';
  const navigate = useNavigate();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [galleryName, setGalleryName] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, gallery: null });
  const [deleting, setDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState({ current: 0, total: 0, percentage: 0, message: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [editDialog, setEditDialog] = useState({ open: false, gallery: null });
  const [editName, setEditName] = useState('');
  const [editDate, setEditDate] = useState('');
  const [updating, setUpdating] = useState(false);
  const [emailDialog, setEmailDialog] = useState({ open: false, gallery: null });
  const [currentEmail, setCurrentEmail] = useState('');
  const [emailList, setEmailList] = useState([]);
  const [emailMessage, setEmailMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendingProgress, setSendingProgress] = useState({ current: 0, total: 0 });
  const [generatingSimpleToken, setGeneratingSimpleToken] = useState(null);

  // Computed stats for hero section
  const stats = useMemo(() => {
    const totalPhotos = galleries.reduce((sum, g) => sum + (g.photo_count || 0), 0);
    const portfolioCount = galleries.filter(g => g.is_portfolio).length;
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const recentCount = galleries.filter(g => new Date(g.created_at || g.event_date) > thirtyDaysAgo).length;
    const finalizedCount = galleries.filter(g => g.finalized).length;

    return { totalPhotos, portfolioCount, recentCount, finalizedCount };
  }, [galleries]);

  const handleTogglePortfolio = async (galleryId, currentStatus, event) => {
    event.stopPropagation();
    
    try {
      await axios.put(
        `${backendUrl}/api/galleries/${galleryId}/portfolio`,
        { is_portfolio: !currentStatus },
        {
          ...axiosConfig,
          params: { is_portfolio: !currentStatus }
        }
      );
      
      toast.success(!currentStatus ? 'Added to portfolio showcase' : 'Removed from portfolio showcase');
      await onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update portfolio status');
    }
  };

  const handleGenerateSimpleToken = async (galleryId) => {
    setGeneratingSimpleToken(galleryId);
    try {
      const response = await axios.post(
        `${backendUrl}/api/admin/gallery/${galleryId}/generate-simple-token`,
        {},
        axiosConfig
      );
      
      toast.success('Simple share link generated!');
      
      // Refresh galleries to get the new simple_share_token
      await loadGalleries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate simple link');
    } finally {
      setGeneratingSimpleToken(null);
    }
  };

  const copyFindMeLink = (e, findMeToken) => {
    e.stopPropagation();
    const shareUrl = `${window.location.origin}/findme/${findMeToken}`;
    
    // Try modern Clipboard API first, fallback to execCommand
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(shareUrl)
        .then(() => toast.success('Find Me link copied to clipboard!'))
        .catch(() => {
          // Fallback to execCommand
          const textArea = document.createElement('textarea');
          textArea.value = shareUrl;
          textArea.style.position = 'fixed';
          textArea.style.top = '0';
          textArea.style.left = '0';
          textArea.style.opacity = '0';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          try {
            document.execCommand('copy');
            toast.success('Find Me link copied!');
          } catch (err) {
            toast.error('Failed to copy link');
          }
          document.body.removeChild(textArea);
        });
    } else {
      // Old browser fallback
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success('Find Me link copied!');
      } catch (err) {
        toast.error('Failed to copy link');
      }
      document.body.removeChild(textArea);
    }
  };

  const copySimpleLink = (e, simpleToken) => {
    e.stopPropagation();
    const shareUrl = `${window.location.origin}/simple/${simpleToken}`;
    
    // Try modern Clipboard API first, fallback to execCommand
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(shareUrl)
        .then(() => toast.success('Simple link copied!'))
        .catch(() => {
          // Fallback to execCommand
          const textArea = document.createElement('textarea');
          textArea.value = shareUrl;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          document.body.appendChild(textArea);
          textArea.select();
          try {
            document.execCommand('copy');
            toast.success('Simple link copied!');
          } catch (err) {
            toast.error('Failed to copy link');
          }
          document.body.removeChild(textArea);
        });
    } else {
      // Use execCommand fallback directly
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success('Simple link copied!');
      } catch (err) {
        toast.error('Failed to copy link');
      }
      document.body.removeChild(textArea);
    }
  };

  // Filter galleries by search query
  const filteredGalleries = galleries.filter(gallery =>
    gallery.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreateGallery = async () => {
    if (!galleryName.trim() || !eventDate) {
      toast.error('Please fill in all fields');
      return;
    }

    setCreating(true);
    try {
      await axios.post(
        `${backendUrl}/api/galleries`,
        { name: galleryName, event_date: eventDate },
        axiosConfig
      );
      
      toast.success('Gallery created successfully!');
      setShowCreateDialog(false);
      setGalleryName('');
      setEventDate('');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create gallery');
    } finally {
      setCreating(false);
    }
  };

  const handleEditGallery = async () => {
    if (!editName.trim() || !editDate) {
      toast.error('Please fill in all fields');
      return;
    }

    setUpdating(true);
    try {
      await axios.put(
        `${backendUrl}/api/galleries/${editDialog.gallery.id}`,
        { name: editName, event_date: editDate },
        axiosConfig
      );
      
      toast.success('Gallery updated successfully!');
      setEditDialog({ open: false, gallery: null });
      setEditName('');
      setEditDate('');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update gallery');
    } finally {
      setUpdating(false);
    }
  };

  const handleAddEmail = () => {
    const email = currentEmail.trim();
    
    if (!email) {
      toast.error('Please enter an email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Check for duplicates
    if (emailList.includes(email)) {
      toast.error('This email has already been added');
      return;
    }

    setEmailList([...emailList, email]);
    setCurrentEmail('');
    toast.success('Email added to list');
  };

  const handleRemoveEmail = (emailToRemove) => {
    setEmailList(emailList.filter(e => e !== emailToRemove));
    toast.success('Email removed from list');
  };

  const handleSendGalleryInvitation = async () => {
    if (emailList.length === 0) {
      toast.error('Please add at least one email address');
      return;
    }

    setSending(true);
    setSendingProgress({ current: 0, total: emailList.length });

    try {
      let successCount = 0;
      let failedEmails = [];

      // Send invitations one by one with progress tracking
      for (let i = 0; i < emailList.length; i++) {
        const email = emailList[i];
        setSendingProgress({ current: i + 1, total: emailList.length });

        try {
          const response = await axios.post(
            `${backendUrl}/api/admin/gallery/${emailDialog.gallery.id}/send-invitation`,
            { 
              emails: [email],
              message: emailMessage.trim()
            },
            axiosConfig
          );

          if (response.data.sent > 0) {
            successCount++;
          } else {
            failedEmails.push(email);
          }
        } catch (error) {
          console.error(`Failed to send to ${email}:`, error);
          failedEmails.push(email);
        }
      }

      // Show results
      if (successCount === emailList.length) {
        toast.success(`✅ Successfully sent ${successCount} invitation(s)!`);
      } else if (successCount > 0) {
        toast.warning(`Sent ${successCount} invitation(s). ${failedEmails.length} failed.`);
      } else {
        toast.error('Failed to send invitations. Please check your email settings.');
      }

      // Close dialog and reset on success
      if (successCount > 0) {
        setEmailDialog({ open: false, gallery: null });
        setEmailList([]);
        setCurrentEmail('');
        setEmailMessage('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitations');
    } finally {
      setSending(false);
      setSendingProgress({ current: 0, total: 0 });
    }
  };

  const handleDeleteGallery = async () => {
    if (!deleteDialog.gallery) return;

    setDeleting(true);
    setDeleteProgress({ current: 0, total: 0, percentage: 0, message: 'Starting deletion...' });
    
    try {
      const token = localStorage.getItem('token');
      const eventSource = new EventSource(
        `${backendUrl}/api/galleries/${deleteDialog.gallery.id}/delete-stream?token=${token}`
      );

      let completed = false;

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.status === 'deleting' || data.status === 'starting') {
            setDeleteProgress({
              current: data.current || 0,
              total: data.total || 0,
              percentage: data.percentage || 0,
              message: data.message || 'Deleting...'
            });
          } else if (data.status === 'complete') {
            completed = true; // Mark as successfully completed
            setDeleteProgress({
              current: data.total,
              total: data.total,
              percentage: 100,
              message: 'Gallery deleted successfully!'
            });
            
            setTimeout(() => {
              eventSource.close();
              toast.success('Gallery deleted successfully');
              setDeleteDialog({ open: false, gallery: null });
              setDeleting(false);
              setDeleteProgress({ current: 0, total: 0, percentage: 0, message: '' });
              onRefresh();
            }, 1500);
          } else if (data.status === 'error' || data.error) {
            eventSource.close();
            toast.error(data.message || data.error || 'Failed to delete gallery');
            setDeleting(false);
            setDeleteProgress({ current: 0, total: 0, percentage: 0, message: '' });
          }
        } catch (e) {
          console.error('Error parsing progress data:', e);
        }
      };

      eventSource.onerror = (error) => {
        console.error('EventSource error:', error);
        eventSource.close();
        
        // Only show error if deletion didn't complete successfully
        if (!completed) {
          toast.error('Connection error during deletion');
          setDeleting(false);
          setDeleteProgress({ current: 0, total: 0, percentage: 0, message: '' });
        }
      };
      
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete gallery');
      setDeleting(false);
      setDeleteProgress({ current: 0, total: 0, percentage: 0, message: '' });
    }
  };

  return (
    <div data-testid="galleries-view" className="font-tac-body">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="font-tac-display" style={{ fontWeight: 800, fontSize: 26, color: '#141414', letterSpacing: '-0.015em' }}>
              {isAdmin ? 'Gallery Dashboard' : 'Your Galleries'}
            </h1>
            <p style={{ fontSize: 13.5, color: '#55555C', marginTop: 4 }}>
              {isAdmin ? 'Manage every event gallery from one place.' : 'Galleries assigned to you.'}
            </p>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            {isAdmin && (
            <DialogTrigger asChild>
              <Btn variant="primary" size="md" data-testid="create-gallery-button">
                <Plus className="w-4 h-4" /> Create Gallery
              </Btn>
            </DialogTrigger>
            )}
            <DialogContent className="bg-white dark:bg-slate-800">
              <DialogHeader>
                <DialogTitle>Create New Gallery</DialogTitle>
                <DialogDescription>
                  Create a new gallery to organize your photos
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Gallery Name</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Wedding Photography 2026"
                    value={galleryName}
                    onChange={(e) => setGalleryName(e.target.value)}
                    data-testid="gallery-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date">Event Date</Label>
                  <Input
                    id="date"
                    type="date"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                    data-testid="gallery-date-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateGallery}
                  disabled={creating}
                  className="text-white"
                  style={{ backgroundColor: '#BF212F' }}
                  data-testid="gallery-create-submit"
                >
                  {creating ? 'Creating...' : 'Create Gallery'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Hero Stats Bento Grid (one carbon hero tile) */}
        {isAdmin && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatTile label="Galleries" value={galleries.length} icon={<FolderOpen className="w-4 h-4" />} accent="#2563EB" />
            <StatTile label="Total Photos" value={stats.totalPhotos.toLocaleString()} icon={<ImageIcon className="w-4 h-4" />} accent="#34D399" hero />
            <StatTile label="In Portfolio" value={stats.portfolioCount} icon={<Star className="w-4 h-4" />} accent="#7C3AED" />
            <StatTile label="This Month" value={stats.recentCount} icon={<Calendar className="w-4 h-4" />} accent="#F2A900" />
          </div>
        )}

        {/* Search Bar */}
        <div style={{ maxWidth: 420 }}>
          <div className="flex items-center gap-2.5" style={{ border: '1px solid #E4E4E9', borderRadius: 9, background: '#FBFBFC', padding: '0 13px' }}>
            <Search className="w-4 h-4" style={{ color: '#9A9AA0' }} />
            <input
              type="text"
              placeholder="Search galleries…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="gallery-search-input"
              style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: '#141414', padding: '12px 0' }}
            />
          </div>
        </div>
      </div>

      {/* Galleries Grid */}
      {filteredGalleries.length === 0 && searchQuery ? (
        <div className="text-center py-20">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4" style={{ background: '#fff', border: '1px solid #E4E4E9' }}>
            <Search className="w-9 h-9" style={{ color: '#9A9AA0' }} />
          </div>
          <h3 className="font-tac-display" style={{ fontWeight: 800, fontSize: 19, color: '#141414', marginBottom: 6 }}>No galleries found</h3>
          <p style={{ color: '#55555C' }}>Try a different search term.</p>
        </div>
      ) : filteredGalleries.length === 0 ? (
        <div className="text-center py-20">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4" style={{ background: '#fff', border: '1px solid #E4E4E9' }}>
            <FolderOpen className="w-9 h-9" style={{ color: '#9A9AA0' }} />
          </div>
          <h3 className="font-tac-display" style={{ fontWeight: 800, fontSize: 19, color: '#141414', marginBottom: 6 }}>No galleries yet</h3>
          <p style={{ color: '#55555C', marginBottom: 16 }}>
            {isAdmin ? 'Create your first gallery to get started.' : 'You have not been assigned to any galleries yet.'}
          </p>
          {isAdmin && (
            <Btn variant="primary" onClick={() => setShowCreateDialog(true)}><Plus className="w-4 h-4" /> Create your first gallery</Btn>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredGalleries.map((gallery) => (
            <Card
              key={gallery.id}
              className="group overflow-hidden"
              style={{ background: '#fff', border: '1px solid #E4E4E9', borderRadius: 14, boxShadow: '0 1px 2px rgba(20,20,20,0.04)' }}
              data-testid={`gallery-card-${gallery.id}`}
            >
              {/* Featured Photo with Hover Overlay */}
              <div className="relative h-48 overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800">
                {(gallery.cover_image_url || gallery.featured_photo_thumbnail) ? (
                  <img
                    src={
                      gallery.cover_image_url
                        ? gallery.cover_image_url
                        : gallery.featured_photo_thumbnail.startsWith('http')
                          ? gallery.featured_photo_thumbnail
                          : `${backendUrl}/api/photos/file/thumbnails/${gallery.featured_photo_thumbnail.split('/').pop()}`
                    }
                    alt={gallery.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    style={{
                      objectPosition: (!gallery.cover_image_url && gallery.featured_photo_focal_point)
                        ? `${gallery.featured_photo_focal_point.x}% ${gallery.featured_photo_focal_point.y}%`
                        : 'center center'
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FolderOpen className="w-16 h-16 text-slate-300 dark:text-slate-600" />
                  </div>
                )}

                {/* Status Badges */}
                <div className="absolute top-3 right-3 flex gap-2">
                  {/* Sharing off is the one badge that changes what a client
                      can do right now, so it leads. */}
                  {gallery.sharing_enabled === false && <StatusPill tone="danger">Sharing off</StatusPill>}
                  {gallery.is_portfolio && <StatusPill tone="scheduled">Portfolio</StatusPill>}
                  {gallery.finalized && <StatusPill tone="success">Finalized</StatusPill>}
                </div>

                {/* Hover Overlay with Quick Actions */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4 flex justify-center gap-2">
                    <Button
                      size="sm"
                      className="bg-white/90 hover:bg-white text-slate-800 h-8"
                      onClick={() => navigate(`/gallery/${gallery.id}`)}
                    >
                      Open Gallery
                    </Button>
                  </div>
                </div>
              </div>

              {/* Card Content */}
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-tac-heading truncate" style={{ fontWeight: 700, fontSize: 16, color: '#141414' }}>
                      {gallery.name}
                    </h3>
                    <div className="flex items-center gap-3 mt-1 font-tac-mono" style={{ fontSize: 11.5, color: '#9A9AA0', letterSpacing: '0.02em' }}>
                      <span className="flex items-center gap-1">
                        <ImageIcon className="w-3.5 h-3.5" />
                        {gallery.photo_count}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(gallery.event_date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' }).toUpperCase()}
                      </span>
                    </div>
                  </div>

                  {/* Actions Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate(`/gallery/${gallery.id}`)}>
                        <FolderOpen className="w-4 h-4 mr-2" />
                        Open Gallery
                      </DropdownMenuItem>
                      {isAdmin && (
                        <DropdownMenuItem onClick={() => {
                          setEditDialog({ open: true, gallery });
                          setEditName(gallery.name);
                          setEditDate(gallery.event_date);
                        }}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit Details
                        </DropdownMenuItem>
                      )}
                      {gallery.share_token && (
                        <DropdownMenuItem onClick={() => setEmailDialog({ open: true, gallery })}>
                          <Mail className="w-4 h-4 mr-2" />
                          Email Links
                        </DropdownMenuItem>
                      )}
                      {isAdmin && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setDeleteDialog({ open: true, gallery })}
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Gallery
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Share Links Section - Compact */}
                {gallery.share_token && (
                  <div className="pt-3 border-t border-slate-100 dark:border-slate-700">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="w-full justify-between h-9">
                          <span className="flex items-center gap-2">
                            <Share2 className="w-4 h-4" />
                            Share Links
                          </span>
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-72">
                        <DropdownMenuLabel>Copy Share Link</DropdownMenuLabel>
                        <DropdownMenuSeparator />

                        {/* Full Gallery Link */}
                        <DropdownMenuItem onClick={(e) => {
                          e.preventDefault();
                          const shareUrl = `${window.location.origin}/share/${gallery.share_token}`;
                          navigator.clipboard.writeText(shareUrl)
                            .then(() => toast.success('Full gallery link copied!'))
                            .catch(() => toast.error('Failed to copy'));
                        }}>
                          <div className="flex items-center gap-2 w-full">
                            <div className="w-2 h-2 rounded-full bg-orange-500" />
                            <span className="flex-1">Full Gallery</span>
                            <Copy className="w-3 h-3 text-slate-400" />
                          </div>
                        </DropdownMenuItem>

                        {/* Simple Link */}
                        {gallery.simple_share_token && (
                          <DropdownMenuItem onClick={(e) => {
                            e.preventDefault();
                            copySimpleLink(e, gallery.simple_share_token);
                          }}>
                            <div className="flex items-center gap-2 w-full">
                              <div className="w-2 h-2 rounded-full bg-blue-500" />
                              <span className="flex-1">Simple Gallery</span>
                              <Copy className="w-3 h-3 text-slate-400" />
                            </div>
                          </DropdownMenuItem>
                        )}

                        {/* Find Me Link */}
                        {gallery.find_me_token && (
                          <DropdownMenuItem onClick={(e) => {
                            e.preventDefault();
                            copyFindMeLink(e, gallery.find_me_token);
                          }}>
                            <div className="flex items-center gap-2 w-full">
                              <div className="w-2 h-2 rounded-full bg-purple-500" />
                              <span className="flex-1">Find My Photos</span>
                              <Copy className="w-3 h-3 text-slate-400" />
                            </div>
                          </DropdownMenuItem>
                        )}

                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => window.open(`/share/${gallery.share_token}`, '_blank')}>
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Open in New Tab
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </CardContent>

              {/* Card Footer */}
              <CardFooter className="p-4 pt-0 flex items-center justify-between">
                {/* Portfolio Toggle (admin only) */}
                {isAdmin ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => handleTogglePortfolio(gallery.id, gallery.is_portfolio, e)}
                      className="relative inline-flex items-center rounded-full transition-colors"
                      style={{ height: 20, width: 34, background: gallery.is_portfolio ? '#BF212F' : '#D7D7DD' }}
                      title="Toggle Portfolio"
                    >
                      <span className="inline-block rounded-full bg-white transition-transform" style={{ height: 14, width: 14, transform: gallery.is_portfolio ? 'translateX(17px)' : 'translateX(3px)' }} />
                    </button>
                    <span style={{ fontSize: 11.5, color: '#55555C' }}>Portfolio</span>
                  </div>
                ) : (
                  <span />
                )}

                <button
                  onClick={() => navigate(`/gallery/${gallery.id}`)}
                  className="font-tac-heading text-white"
                  style={{ background: '#BF212F', borderRadius: 7, padding: '7px 13px', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
                  data-testid={`view-gallery-${gallery.id}`}
                >
                  Manage →
                </button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog 
        open={deleteDialog.open} 
        onOpenChange={(open) => {
          // Prevent closing while deleting
          if (!deleting) {
            setDeleteDialog({ ...deleteDialog, open });
          }
        }}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Gallery</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteDialog.gallery?.name}"? 
              This will permanently delete {deleteDialog.gallery?.photo_count || 0} photos. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {/* Progress Bar */}
          {deleting && (
            <div className="space-y-3 py-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{deleteProgress.message}</span>
                <span className="font-semibold text-red-600">{deleteProgress.percentage}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-red-500 to-red-600 transition-all duration-300 ease-out"
                  style={{ width: `${deleteProgress.percentage}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 text-center">
                {deleteProgress.current} of {deleteProgress.total} photos deleted
              </p>
            </div>
          )}
          
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteGallery}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600"
            >
              {deleting ? 'Deleting...' : 'Delete Gallery'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Gallery Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ ...editDialog, open })}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Edit Gallery</DialogTitle>
            <DialogDescription>
              Update the gallery name and event date
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Gallery Name</Label>
              <Input
                id="edit-name"
                placeholder="e.g., Wedding Photography 2025"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                data-testid="edit-gallery-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-date">Event Date</Label>
              <Input
                id="edit-date"
                type="date"
                value={editDate}
                onChange={(e) => setEditDate(e.target.value)}
                data-testid="edit-gallery-date-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, gallery: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleEditGallery}
              disabled={updating}
              className="text-white"
              style={{backgroundColor: '#BF212F'}}
              data-testid="gallery-edit-submit"
            >
              {updating ? 'Updating...' : 'Update Gallery'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Gallery Dialog */}
      <Dialog open={emailDialog.open} onOpenChange={(open) => {
        if (!sending) {
          setEmailDialog({ ...emailDialog, open });
          if (!open) {
            setEmailList([]);
            setCurrentEmail('');
            setEmailMessage('');
          }
        }
      }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="font-tac-display" style={{ fontWeight: 800, fontSize: 22, letterSpacing: '-0.015em', color: '#141414' }}>
              Email Gallery Link
            </DialogTitle>
            <DialogDescription>
              Send invitation emails to view "{emailDialog.gallery?.name}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Add Email Input */}
            <div className="space-y-2">
              <Label htmlFor="current-email">
                Add Email Address <span className="text-red-500">*</span>
              </Label>
              <div className="flex gap-2">
                <Input
                  id="current-email"
                  type="email"
                  placeholder="recipient@email.com"
                  value={currentEmail}
                  onChange={(e) => setCurrentEmail(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddEmail();
                    }
                  }}
                  disabled={sending}
                  className="flex-1"
                />
                <Button
                  onClick={handleAddEmail}
                  disabled={sending || !currentEmail.trim()}
                  variant="outline"
                  type="button"
                >
                  Add
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                Enter one email at a time and click "Add" or press Enter
              </p>
            </div>

            {/* Email List */}
            {emailList.length > 0 && (
              <div className="space-y-2">
                <Label>Recipients ({emailList.length})</Label>
                <div className="border border-slate-200 rounded-md p-3 max-h-[200px] overflow-y-auto bg-slate-50">
                  <div className="space-y-2">
                    {emailList.map((email, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-white px-3 py-2 rounded border border-slate-200"
                      >
                        <span className="text-sm text-slate-700">{email}</span>
                        <Button
                          onClick={() => handleRemoveEmail(email)}
                          disabled={sending}
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 hover:bg-red-50 hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  CC will automatically be sent to hello@turbo360.com.au
                </p>
              </div>
            )}

            {/* Personal Message */}
            <div className="space-y-2">
              <Label htmlFor="email-message">
                Personal Message (Optional)
              </Label>
              <textarea
                id="email-message"
                placeholder="Add a personal message to include in the email..."
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                disabled={sending}
                className="w-full min-h-[80px] px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#BF212F] disabled:bg-slate-100 disabled:cursor-not-allowed"
              />
            </div>

            {/* Sending Progress */}
            {sending && (
              <div className="space-y-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-blue-800 font-medium">
                    Sending invitations...
                  </span>
                  <span className="text-blue-600">
                    {sendingProgress.current} / {sendingProgress.total}
                  </span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${(sendingProgress.current / sendingProgress.total) * 100}%`
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setEmailDialog({ open: false, gallery: null });
                setEmailList([]);
                setCurrentEmail('');
                setEmailMessage('');
              }}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSendGalleryInvitation}
              disabled={sending || emailList.length === 0}
              className="text-white"
              style={{backgroundColor: '#BF212F'}}
            >
              {sending ? `Sending ${sendingProgress.current}/${sendingProgress.total}...` : `Send to ${emailList.length} Recipient${emailList.length !== 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleriesView;
